import React from 'react';
import { MailIcon, MessageSquareIcon, PhoneIcon, UsersIcon, VideoIcon } from 'lucide-react';
import type { ActivityEntry } from '../../data/activityHistory';
import type { Channel, Outcome } from '../../types/portal';
const channelMeta: Record<Channel, {
  label: string;
  icon: typeof PhoneIcon;
}> = {
  call: {
    label: 'Call',
    icon: PhoneIcon
  },
  text: {
    label: 'Text',
    icon: MessageSquareIcon
  },
  email: {
    label: 'Email',
    icon: MailIcon
  },
  'in-person': {
    label: 'In person',
    icon: UsersIcon
  },
  video: {
    label: 'Video',
    icon: VideoIcon
  }
};
const outcomeMeta: Record<Outcome, {
  label: string;
  classes: string;
}> = {
  connected: {
    label: 'Connected',
    classes: 'bg-success-50 text-success-700 ring-success-200'
  },
  'left-voicemail': {
    label: 'Left voicemail',
    classes: 'bg-navy-50 text-navy-600 ring-navy-200'
  },
  'no-answer': {
    label: 'No answer',
    classes: 'bg-warn-50 text-warn-700 ring-warn-200'
  },
  replied: {
    label: 'Replied',
    classes: 'bg-success-50 text-success-700 ring-success-200'
  },
  'sent-no-reply': {
    label: 'Sent — no reply',
    classes: 'bg-navy-50 text-navy-600 ring-navy-200'
  },
  bounced: {
    label: 'Bounced',
    classes: 'bg-danger-50 text-danger-700 ring-danger-200'
  },
  met: {
    label: 'Met in person',
    classes: 'bg-success-50 text-success-700 ring-success-200'
  },
  'no-show': {
    label: 'No show',
    classes: 'bg-warn-50 text-warn-700 ring-warn-200'
  },
  'meeting-set': {
    label: 'Meeting set',
    classes: 'bg-teal-50 text-teal-700 ring-teal-200'
  },
  'application-started': {
    label: 'Application started',
    classes: 'bg-teal-50 text-teal-700 ring-teal-200'
  },
  'not-interested': {
    label: 'Not interested',
    classes: 'bg-danger-50 text-danger-700 ring-danger-200'
  }
};
export function ActivityTimeline({
  entries
}: {
  entries: ActivityEntry[];
}) {
  if (entries.length === 0) {
    return <div className="rounded-xl border border-dashed border-navy-200 p-6 text-center" data-id="element-233" data-mp="15yn6jadz70ij">
        <p className="text-sm font-semibold text-navy-800" data-id="element-234" data-mp="13l9wmj9t0g7o">
          No communication logged yet
        </p>
        <p className="mx-auto mt-1 max-w-sm text-sm text-navy-500" data-id="element-235" data-mp="zhbx47gkh3e3">
          Call or text from your own phone, then log it here so the activity
          history and your goals stay accurate.
        </p>
      </div>;
  }
  return <ol className="space-y-4" data-id="element-236" data-mp="1n8hit396upwb">
      {entries.map(entry => {
      const channel = channelMeta[entry.channel];
      const outcome = outcomeMeta[entry.outcome];
      const Icon = channel.icon;
      return <li key={entry.id} className="flex gap-3" data-id="element-237" data-mp="1di4tyyuccakd">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-navy-50 text-navy-600" data-id="element-238" data-mp="1dgla6y9h7hdu">
              <Icon className="h-4 w-4" aria-hidden="true" data-id="element-239" data-mp="25yp7igc55dsz" />
            </span>
            <div className="min-w-0 flex-1 border-b border-navy-100 pb-4 last:border-0 last:pb-0" data-id="element-240" data-mp="260s7nt70cnbf">
              <div className="flex flex-wrap items-center gap-2" data-id="element-241" data-mp="216fm9dlz11x1">
                <p className="text-sm font-semibold text-navy-900" data-id="element-242" data-mp="28kyafxjvy7qe">
                  {channel.label}
                </p>
                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ${outcome.classes}`} data-id="element-243" data-mp="2dkko283mlpzq">
                  {outcome.label}
                </span>
                <span className="text-xs text-navy-500" data-id="element-244" data-mp="2rj126k3tiirw">{entry.timestamp}</span>
              </div>
              <p className="mt-1 text-sm text-navy-700" data-id="element-245" data-mp="2wgkma0n75prr">{entry.note}</p>
              <p className="mt-1 text-xs text-navy-400" data-id="element-246" data-mp="318ccga2fxthd">
                Logged by {entry.author}
              </p>
            </div>
          </li>;
    })}
    </ol>;
}